import { useCallback, useEffect, useRef, useState } from "react"
import Api from "../api/api"
import { BetaTester, UserSearchResult } from "../api/models"
import UserFilter from "./widgets/UserFilter"
import UserChip from "./widgets/UserChip"
import ErrorDialog from "./widgets/ErrorDialog"
import Spinner from "./widgets/Spinner"
import { backdropDismiss } from "../utils/backdropDismiss"

// Adds an existing user to the org's beta-tester list — the house "add"
// pattern: a conditionally mounted modal opened from the section header. The
// success step reports the beta licenses deposited for running auto-deposit
// betas.
function AddTesterDialog({ orgId, existingIds, accessToken, onClose, onAdded }: {
  orgId: string
  existingIds: string[]
  accessToken: string
  onClose: () => void
  onAdded: () => void
}) {
  const dialogRef = useRef<HTMLDialogElement>(null)
  const [adding, setAdding] = useState<UserSearchResult | null>(null)
  const [step, setStep] = useState<"form" | "saving" | "success" | "error">("form")
  const [successMessage, setSuccessMessage] = useState("")
  const [errorMessage, setErrorMessage] = useState("")

  useEffect(() => {
    dialogRef.current?.showModal()
  }, [])

  const busy = step === "saving"

  const handleSubmit = async () => {
    if (adding === null) return
    setStep("saving")
    setErrorMessage("")
    try {
      const { reconciles } = await Api.addBetaTester({ orgId, userId: adding.id, accessToken })
      const deposited = reconciles.reduce((acc, r) => acc + r.depositedCount, 0)
      onAdded()
      setSuccessMessage(deposited > 0
        ? `Added — deposited ${deposited} beta license${deposited === 1 ? "" : "s"} for the running beta${reconciles.length === 1 ? "" : "s"}.`
        : "Added.")
      setStep("success")
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : "unknown error")
      setStep("error")
    }
  }

  return (
    <dialog
      ref={dialogRef}
      className="redeem-dialog redeem-dialog-overflow"
      onClose={onClose}
      {...backdropDismiss(() => { if (!busy) dialogRef.current?.close() })}
    >
      <div className="redeem-dialog-content">
        <button className="redeem-dialog-close" onClick={() => dialogRef.current?.close()} disabled={busy} aria-label="Close">&times;</button>
        <h2 className="redeem-dialog-title">Add Beta Tester</h2>

        {step !== "success" && (
          <>
            <div className="generate-codes-form">
              <div className="generate-codes-field">
                <span className="generate-codes-label">User</span>
                <UserFilter
                  selected={adding}
                  onSelect={setAdding}
                  accessToken={accessToken}
                  placeholder="ID, name or email…"
                  disabledIds={existingIds}
                  disabledHint="already a tester"
                />
                <span className="generate-codes-hint">
                  Running betas with auto-deposit on grant them a beta license immediately.
                </span>
              </div>
            </div>

            {step === "error" && errorMessage && (
              <p className="generate-codes-error">{errorMessage}</p>
            )}

            <div className="redeem-actions">
              <button onClick={() => dialogRef.current?.close()} disabled={busy}>Cancel</button>
              <button className="btn-upgrade" disabled={adding === null || busy} onClick={handleSubmit}>
                {busy ? <><Spinner size={12} /> Adding…</> : "Add"}
              </button>
            </div>
          </>
        )}

        {step === "success" && (
          <>
            <p className="generate-codes-success">{successMessage}</p>
            <div className="redeem-actions">
              <button className="btn-upgrade" onClick={() => dialogRef.current?.close()}>Done</button>
            </div>
          </>
        )}
      </div>
    </dialog>
  )
}

// The organization's beta-tester list (an org-scoped AUDIENCE, deliberately
// separate from organization_members — see the decision record in the
// multi-tenancy plan), embedded on the organization page. Everyone on it
// receives the org's beta versions: promoting with auto-deposit grants
// immediately, later-added testers are granted automatically while a beta
// runs; removal also revokes their licenses in running betas (two-click
// armed Remove), so the list is who's in the beta in both directions.
// Managed through the org-nested endpoints, gated (like member management)
// on org admin — the page mounts this card only for org admins and system
// admins.
export default function BetaTestersCard({ orgId, accessToken }: { orgId: string, accessToken: string }) {
  const [testers, setTesters] = useState<BetaTester[] | undefined>(undefined)
  const [error, setError] = useState(false)
  const [addOpen, setAddOpen] = useState(false)
  const [removingId, setRemovingId] = useState<string | null>(null)
  // two-click confirm: first click arms this tester's Remove button
  const [armedId, setArmedId] = useState<string | null>(null)
  const [notice, setNotice] = useState("")
  const [actionError, setActionError] = useState("")

  const load = useCallback(async () => {
    if (!accessToken) return
    setError(false)
    try {
      const { testers } = await Api.listBetaTesters({ orgId, accessToken })
      setTesters(testers)
    } catch {
      setError(true)
    }
  }, [orgId, accessToken])

  useEffect(() => {
    setTesters(undefined)
    load()
  }, [load])

  const handleRemove = async (tester: BetaTester) => {
    if (armedId !== tester.userId) {
      setArmedId(tester.userId)
      return
    }
    setArmedId(null)
    setRemovingId(tester.userId)
    setActionError("")
    setNotice("")
    try {
      const { revokedCount } = await Api.removeBetaTester({ orgId, userId: tester.userId, accessToken })
      setNotice(revokedCount > 0
        ? `Removed — revoked ${revokedCount} running-beta license${revokedCount === 1 ? "" : "s"}.`
        : "Removed.")
      await load()
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "unknown error")
    } finally {
      setRemovingId(null)
    }
  }

  return (
    <div className="tab-card" style={{ marginTop: 24 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <h2 className="section-title tab-card-tab">Beta Testers</h2>
        <button
          className="card-link"
          style={{ background: "none", border: "none", cursor: "pointer", padding: 0, fontSize: "10pt" }}
          onClick={() => setAddOpen(true)}
        >
          Add tester
        </button>
      </div>
      <div className="tab-card-body">
        <p className="generate-codes-hint" style={{ textAlign: "left", marginBottom: 12 }}>
          Everyone on this list receives the organization's beta versions
          (immediately when a beta runs with auto-deposit on). Removing a
          tester also revokes their licenses in any running betas; licenses
          kept from finished betas stay.
        </p>
        {notice && <p style={{ color: "var(--nfa-blue)", fontSize: "10pt" }}>{notice}</p>}

        {error && testers === undefined ? (
          <p className="empty-state">Error loading beta testers</p>
        ) : testers === undefined ? (
          <p className="empty-state"><Spinner size={16} /> Loading beta testers…</p>
        ) : testers.length === 0 ? (
          <p className="empty-state">No beta testers yet — use "Add tester" to add existing users.</p>
        ) : (
          testers.map((tester) => (
            <div key={tester.userId} className="license-group">
              <div className="product-group-header">
                <h2 className="row-title" style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <UserChip userId={tester.userId} user={tester.user} />
                  {tester.user.enabled === false && <span className="badge">Disabled</span>}
                </h2>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 12, flexWrap: "wrap" }}>
                  {tester.user.email && (
                    <span style={{ color: "var(--nfa-light-grey)", fontSize: "10pt" }}>{tester.user.email}</span>
                  )}
                  <span style={{ color: "var(--nfa-light-grey)", fontSize: "10pt" }}>
                    since {new Date(tester.createdAt).toLocaleDateString()}
                  </span>
                  <button
                    className="card-link-danger"
                    style={{ background: "none", border: "none", cursor: "pointer", padding: 0, fontSize: "10pt", fontWeight: armedId === tester.userId ? "bold" : undefined }}
                    onClick={() => handleRemove(tester)}
                    onBlur={() => setArmedId((prev) => prev === tester.userId ? null : prev)}
                    disabled={removingId === tester.userId}
                  >
                    {removingId === tester.userId ? "Removing…"
                      : armedId === tester.userId ? "Remove & revoke beta licenses?"
                        : "Remove"}
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {actionError && (
        <ErrorDialog message={actionError} onClose={() => setActionError("")} />
      )}

      {addOpen && (
        <AddTesterDialog
          orgId={orgId}
          existingIds={(testers ?? []).map(t => t.userId)}
          accessToken={accessToken}
          onClose={() => setAddOpen(false)}
          onAdded={load}
        />
      )}
    </div>
  )
}
